import React, { useState, useEffect } from 'react';
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Label } from "../../components/ui/label";
import { X } from "lucide-react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cn } from "../../lib/utils";

// Create dialog components directly
const Dialog = DialogPrimitive.Root;
const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <DialogPrimitive.Portal>
    <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm" />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        "fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-white p-6 shadow-lg rounded-lg",
        className
      )}
      {...props}
    >
      {children}
      <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70">
        <X className="h-4 w-4" />
        <span className="sr-only">Close</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPrimitive.Portal>
));
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-1.5 text-center sm:text-left",
      className
    )}
    {...props}
  />
);

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-none tracking-tight",
      className
    )}
    {...props}
  />
));

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn(
      "text-sm text-muted-foreground",
      className
    )}
    {...props}
  />
));

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className
    )}
    {...props}
  />
);

// Create textarea component directly
const Textarea = React.forwardRef<
  HTMLTextAreaElement, 
  React.TextareaHTMLAttributes<HTMLTextAreaElement>
>(({ className, ...props }, ref) => {
  return (
    <textarea
      className={cn(
        "flex min-h-[80px] w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm",
        className
      )}
      ref={ref}
      {...props}
    />
  );
});
Textarea.displayName = "Textarea";

interface MethodologyEditDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: { title: string; description: string; keywords?: string[]; type?: 'straight-up' | 'pathway' }) => void;
  title?: string;
  description?: string;
  keywords?: string[];
  isNew?: boolean;
}

export const MethodologyEditDialog: React.FC<MethodologyEditDialogProps> = ({
  open,
  onClose,
  onSave,
  title: initialTitle = '',
  description: initialDescription = '',
  keywords: initialKeywords = [],
  isNew = false
}) => {
  // Form state
  const [title, setTitle] = useState<string>(initialTitle || '');
  const [description, setDescription] = useState<string>(initialDescription || '');
  const [keywords, setKeywords] = useState<string[]>(initialKeywords || []);
  const [newKeyword, setNewKeyword] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [type, setType] = useState<'straight-up' | 'pathway'>('straight-up');

  // Reset form when dialog opens with new values
  useEffect(() => {
    if (open) {
      setTitle(initialTitle || '');
      setDescription(initialDescription || '');
      setKeywords(initialKeywords || []);
      setNewKeyword('');
      setError(null);
      setType('straight-up');
    }
  }, [open, initialTitle, initialDescription, initialKeywords]);

  // Form submission handler
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!title.trim()) {
      setError("Title is required");
      return;
    }
    
    onSave({ 
      title: title.trim(), 
      description: description.trim(), 
      keywords,
      type: isNew ? type : undefined
    });
  };
  
  // Keyword management
  const handleAddKeyword = (e?: React.MouseEvent) => {
    if (e) e.preventDefault();
    
    if (!newKeyword.trim()) return;
    
    // Don't add duplicates
    if (keywords.some(k => k.toLowerCase() === newKeyword.trim().toLowerCase())) {
      setError("Keyword already exists");
      return;
    }
    
    setKeywords(prev => [...prev, newKeyword.trim().toLowerCase()]);
    setNewKeyword('');
    setError(null);
  };
  
  const handleRemoveKeyword = (keywordToRemove: string) => {
    setKeywords(prev => prev.filter(keyword => keyword !== keywordToRemove));
  };
  
  // Input handlers
  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTitle(e.target.value);
  };

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setDescription(e.target.value);
  };
  
  const handleKeywordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewKeyword(e.target.value);
  };
  
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddKeyword();
    }
  };

  return (
    <Dialog 
      open={open} 
      onOpenChange={(isOpen: boolean) => {
        if (!isOpen) onClose();
      }}
    >
      <DialogContent className="sm:max-w-[500px]" aria-describedby="methodology-edit-description">
        <DialogHeader>
          <DialogTitle>{isNew ? 'Create New Methodology' : 'Edit Methodology'}</DialogTitle>
          <DialogDescription id="methodology-edit-description">
            {isNew ? 'Create a new methodology.' : 'Edit the details of this methodology.'}
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="title">Title</Label>
            <Input
              id="title"
              value={title}
              onChange={handleTitleChange}
              placeholder="Enter methodology title"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={description}
              onChange={handleDescriptionChange}
              placeholder="Enter methodology description"
              rows={3}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="keywords">Keywords</Label>
            <div className="flex gap-2">
              <Input
                id="keywords"
                value={newKeyword}
                onChange={handleKeywordChange}
                onKeyDown={handleKeyDown}
                placeholder="Add keyword"
                className="flex-1"
              />
            <Button
              type="button"
                onClick={(e) => handleAddKeyword(e)}
                variant="outline"
              >
                Add
              </Button>
            </div>
            
            {error && <p className="text-sm text-red-500">{error}</p>}
            
            <div className="flex flex-wrap gap-2 mt-2">
              {keywords && keywords.length > 0 ? (
                keywords.map((keyword, index) => (
                  <Badge 
                    key={index} 
                    variant="secondary"
                    className="flex items-center gap-1 px-2 py-1"
                  >
                    {keyword}
                    <X 
                      size={14} 
                      className="cursor-pointer" 
                      onClick={() => handleRemoveKeyword(keyword)}
                    />
                  </Badge>
                ))
              ) : (
                <p className="text-sm text-gray-500">No keywords added</p>
              )}
            </div>
          </div>
          
          {isNew && (
            <div>
              <Label htmlFor="methodology-type">Type</Label>
              <div className="flex gap-4 mt-2">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="methodology-type"
                    value="straight-up"
                    checked={type === 'straight-up'}
                    onChange={() => setType('straight-up')}
                  />
                  Straight-up
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="methodology-type"
                    value="pathway"
                    checked={type === 'pathway'}
                    onChange={() => setType('pathway')}
                  />
                  Pathway
                </label>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit">
              {isNew ? 'Create' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}; 